import React, {useEffect, useRef} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {
  ChildPolicy,
  FAQResponse
} from '@findhotel/sapi/dist/types/packages/core/src/types'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {NAV_BAR_MOBILE_HEIGHT} from '@vio/atlas/NavBar'
import {linariaMq} from '@vio/atlas/src/utils/breakpoints'
import {Text} from '@vio/atlas/Text'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {RoomSplitType} from '@vio/core/src/room/types/RoomConfiguration'

import {RoomAmenities} from '../RoomAmenities/RoomAmenities'
import {RoomInfo} from '../RoomInfo/RoomInfo'
import {RoomNameWithMultiplier} from '../RoomNameWithMultiplier'
import {OptionalExtrasPolicies} from './OptionalExtrasPolicies'
import {RoomDealDetails} from './RoomDealDetails'
import {DividerComponent, SpacingWrapper} from './roomDetailsOverlayHelpers'

import type {RoomDealDetailsProps} from './RoomDealDetails'
import type {RoomWithoutOffers} from '@vio/core/src/room/types/room'

interface Props {
  room: RoomWithoutOffers
  roomDisplayName?: string
  roomsAmount?: number
  userCountry: string
  isTruncatedLongName?: boolean
  onRoomNameInView?: (isInView: boolean) => void
  faqData?: FAQResponse
  childPolicy?: ChildPolicy
  roomsSplit?: RoomSplitType[]
  offersSectionSlot?: React.ReactNode
  roomDealDetails?: RoomDealDetailsProps
  scrollToDealDetails?: boolean
  hasAllRoomsHaveLabel?: boolean
}

const faqLabels = [
  'ExtraBedsCribs',
  'KidsFriendliness',
  'Breakfast',
  'PetFriendliness'
]

const MultilineRoomName = styled(Text)`
  &.--is-truncated-long-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-line-clamp: 4;
    ${linariaMq.desktopXs} {
      -webkit-line-clamp: 3;
    }
  }
`

const hasFaqWithLabel = (
  faqs: FAQResponse['faqs'],
  labels: string[]
): boolean => {
  return faqs?.some(faq => labels.includes(faq.label)) ?? false
}

export const RoomDetailsOverlayContent = ({
  room,
  roomDisplayName,
  roomsAmount = 1,
  userCountry,
  isTruncatedLongName = true,
  onRoomNameInView,
  faqData,
  roomsSplit = [],
  offersSectionSlot,
  childPolicy,
  roomDealDetails,
  scrollToDealDetails,
  hasAllRoomsHaveLabel
}: Props) => {
  const {isMobile} = useDeviceLayout()
  const {ref: roomNameRef, inView: isRoomNameInView} = useInView({
    threshold: 0
  })
  const dealDetailsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    onRoomNameInView?.(isRoomNameInView)
  }, [isRoomNameInView, onRoomNameInView])

  useEffect(() => {
    if (scrollToDealDetails && dealDetailsRef.current) {
      dealDetailsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [scrollToDealDetails])

  const hasAmenities = (room?.amenitiesList?.length ?? 0) > 0

  const hasOptionalExtras =
    Boolean(faqData) && hasFaqWithLabel(faqData?.faqs ?? [], faqLabels)

  const dividedItems = [
    ...(hasAmenities
      ? // eslint-disable-next-line react/jsx-key
        [<RoomAmenities amenities={room.amenitiesList} />]
      : []),
    ...(hasOptionalExtras && faqData
      ? // eslint-disable-next-line react/jsx-key
        [<OptionalExtrasPolicies faqData={faqData} />]
      : []),
    ...(offersSectionSlot ? [offersSectionSlot] : [])
  ]

  const shouldHideDivider = Boolean(roomDealDetails) && !isMobile

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      paddingTop="s600"
      paddingBottom="s600"
    >
      <div ref={roomNameRef}>
        <Box marginBottom="s400">
          <SpacingWrapper isMobile={isMobile}>
            {roomsAmount > 1 ? (
              <RoomNameWithMultiplier
                roomNameSlot={
                  <MultilineRoomName
                    variant="titleXL"
                    dir="auto"
                    className={cx(
                      isTruncatedLongName && '--is-truncated-long-name'
                    )}
                  >
                    {roomDisplayName ?? room.name}
                  </MultilineRoomName>
                }
                numberOfRooms={roomsAmount}
              />
            ) : (
              <MultilineRoomName
                variant="titleXL"
                dir="auto"
                className={cx(
                  isTruncatedLongName && '--is-truncated-long-name'
                )}
              >
                {roomDisplayName ?? room.name}
              </MultilineRoomName>
            )}
          </SpacingWrapper>
        </Box>
      </div>
      <SpacingWrapper isMobile={isMobile}>
        <RoomInfo
          room={room}
          userCountry={userCountry}
          roomsSplit={roomsSplit}
          childPolicy={childPolicy}
          isMountedOnOverlay
          allRoomsHaveLabel={hasAllRoomsHaveLabel ? 'above' : undefined}
        />
      </SpacingWrapper>
      {!shouldHideDivider && <DividerComponent isMobile={isMobile} />}
      {roomDealDetails && (
        <div
          ref={dealDetailsRef}
          style={
            isMobile
              ? {scrollMarginTop: `${NAV_BAR_MOBILE_HEIGHT}px`}
              : undefined
          }
        >
          <RoomDealDetails {...roomDealDetails} />
        </div>
      )}
      {dividedItems.map((section, index) => (
        <Box key={index}>
          <SpacingWrapper
            isMobile={isMobile}
            isCustomLayout={
              Boolean(offersSectionSlot) && index === dividedItems.length - 1
            }
          >
            {section}
          </SpacingWrapper>
          {index !== dividedItems.length - 1 && (
            <DividerComponent isMobile={isMobile} />
          )}
        </Box>
      ))}
    </Box>
  )
}
